import { formatNumberWithSeparator, parseNumberFromSeparator } from './numberFormat.js'

// ==================== ORDER CALCULATIONS ====================

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const calculateOrderItemTotals = (item = {}) => {
  const quantity = Math.max(0, toFiniteNumber(item.quantity ?? item.qty, 0))
  const unitPrice = Math.max(0, toFiniteNumber(item.unit_price ?? item.price, 0))
  const discountBaseUnitPrice = Math.max(
    0,
    toFiniteNumber(item.base_price ?? item.unit_price ?? item.price, 0),
  )
  // Backend stores Rupiah as BIGINT and truncates fractional Rupiah.
  const baseSubtotal = Math.floor(quantity * unitPrice)
  const discountableSubtotal = Math.floor(quantity * discountBaseUnitPrice)
  const requestedDiscount = Math.max(0, toFiniteNumber(item.discount_amount, 0))
  const discountAmount = Math.min(requestedDiscount, discountableSubtotal)
  const additionalCost = Math.max(0, toFiniteNumber(item.additional_cost, 0))
  const subtotal = Math.max(0, Math.round(baseSubtotal - discountAmount + additionalCost))

  return {
    quantity,
    unitPrice,
    baseSubtotal,
    discountableSubtotal,
    discountAmount,
    additionalCost,
    subtotal,
  }
}

/**
 * Calculate order totals from items and additional data
 * @param {Array} items - Array of order items
 * @param {number} additionalCosts - Additional costs (delivery, etc.)
 * @param {number} discountTotal - Total discount amount
 * @returns {Object} Calculated totals
 */
export const calculateOrderTotals = (items = [], additionalCosts = 0, discountTotal = 0) => {
  const subtotal = items.reduce(
    (sum, item) => sum + calculateOrderItemTotals(item).subtotal,
    0,
  )
  
  const safeAdditionalCosts = Math.max(0, toFiniteNumber(additionalCosts, 0))
  const safeDiscountTotal = Math.max(0, toFiniteNumber(discountTotal, 0))
  const grandTotal = Math.max(0, Math.round(subtotal - safeDiscountTotal + safeAdditionalCosts))
  
  return {
    subtotal,
    additionalCosts: safeAdditionalCosts,
    discountTotal: safeDiscountTotal,
    grandTotal
  }
}

/**
 * Calculate payment totals from payment array
 * @param {Array} payments - Array of payment objects
 * @returns {Object} Payment summary
 */
export const calculatePaymentTotals = (payments = []) => {
  const totalCash = payments.reduce((sum, payment) => sum + (payment.cash_amount || 0), 0)
  const totalElectronic = payments.reduce((sum, payment) => sum + (payment.electronic_amount || 0), 0)
  const totalPaid = totalCash + totalElectronic
  
  return {
    totalCash,
    totalElectronic, 
    totalPaid
  }
}

/**
 * Calculate change amount
 * @param {number} totalPaid - Total amount paid
 * @param {number} grandTotal - Order grand total
 * @returns {number} Change amount (positive) or remaining balance (negative)
 */
export const calculateChange = (totalPaid, grandTotal) => {
  return totalPaid - grandTotal
}

/**
 * Determine payment status based on amounts
 * @param {number} totalPaid - Total amount paid
 * @param {number} grandTotal - Order grand total
 * @returns {string} Payment status (UNPAID, PARTIAL, PAID, REFUNDED)
 */
export const getPaymentStatus = (totalPaid, grandTotal) => {
  if (totalPaid <= 0) return 'UNPAID'
  if (totalPaid < grandTotal) return 'PARTIAL'
  if (totalPaid >= grandTotal) return 'PAID'
  return 'UNPAID'
}

// ==================== ITEM VALIDATION ====================

/**
 * Validate order item data
 * @param {Object} item - Order item object
 * @returns {Object} Validation result with isValid and errors
 */
export const validateOrderItem = (item) => {
  const errors = []
  
  if (!item.item_type || !['PRODUCT', 'SERVICE'].includes(item.item_type)) {
    errors.push('Item type must be PRODUCT or SERVICE')
  }
  
  if (item.item_type === 'PRODUCT' && !item.product_id) {
    errors.push('Product ID is required for product items')
  }
  
  if (item.item_type === 'SERVICE' && !item.service_id) {
    errors.push('Service ID is required for service items')
  }
  
  if (!item.quantity || item.quantity <= 0) {
    errors.push('Quantity must be greater than 0')
  }
  
  if (!item.unit_price || item.unit_price < 0) {
    errors.push('Unit price must be 0 or greater')
  }

  const itemTotals = calculateOrderItemTotals(item)
  if (toFiniteNumber(item.discount_amount, 0) < 0) {
    errors.push('Item discount must not be negative')
  }
  if (toFiniteNumber(item.additional_cost, 0) < 0) {
    errors.push('Item additional cost must not be negative')
  }
  if (toFiniteNumber(item.discount_amount, 0) > itemTotals.discountableSubtotal) {
    errors.push('Item discount must not exceed item base total')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validate complete order data
 * @param {Object} orderData - Order data object
 * @returns {Object} Validation result with isValid and errors
 */
export const validateOrderData = (orderData) => {
  const errors = []
  
  if (!orderData.seller_type || !['INTERNAL', 'EXTERNAL', 'ONLINE'].includes(orderData.seller_type)) {
    errors.push('Seller type must be INTERNAL, EXTERNAL, or ONLINE')
  }
  
  if (!orderData.items || !Array.isArray(orderData.items) || orderData.items.length === 0) {
    errors.push('Order must have at least one item')
  } else {
    orderData.items.forEach((item, index) => {
      const itemValidation = validateOrderItem(item)
      if (!itemValidation.isValid) {
        errors.push(`Item ${index + 1}: ${itemValidation.errors.join(', ')}`)
      }
    })
  }
  
  if (!orderData.customer_id && !orderData.customer_name) {
    errors.push('Either customer_id or customer_name must be provided')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

// ==================== PAYMENT HELPERS ====================

/**
 * Create payment object with proper structure
 * @param {string} method - Payment method (CASH, QRIS, TRANSFER, MIXED)
 * @param {number} cashAmount - Cash amount
 * @param {number} electronicAmount - Electronic amount
 * @param {Object} options - Additional options
 * @returns {Object} Payment object
 */
export const createPaymentObject = (method, cashAmount = 0, electronicAmount = 0, options = {}) => {
  return {
    payment_method: method,
    cash_amount: cashAmount,
    electronic_amount: electronicAmount,
    account_destination: options.account_destination || '',
    payment_proof_url: options.payment_proof_url || '',
    notes: options.notes || ''
  }
}

/**
 * Split payment across multiple methods
 * @param {number} totalAmount - Total amount to pay
 * @param {Array} paymentMethods - Array of payment method configurations
 * @returns {Array} Array of payment objects
 */
export const splitPayment = (totalAmount, paymentMethods = []) => {
  const payments = []
  let remainingAmount = totalAmount
  
  paymentMethods.forEach((method, index) => {
    const isLast = index === paymentMethods.length - 1
    const amount = isLast ? remainingAmount : Math.min(method.amount, remainingAmount)
    
    if (amount > 0) {
      payments.push(createPaymentObject(
        method.type,
        method.type === 'CASH' || method.type === 'MIXED' ? amount : 0,
        method.type === 'QRIS' || method.type === 'TRANSFER' || method.type === 'MIXED' ? amount : 0,
        method.options || {}
      ))
      remainingAmount -= amount
    }
  })
  
  return payments
}

// ==================== FORMATTING HELPERS ====================

/**
 * Format currency amount for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'IDR')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'IDR') => {
  if (amount === null || amount === undefined) return `${currency} 0`
  return `${currency} ${formatNumberWithSeparator(amount)}`
}

/**
 * Format payment method for display
 * @param {string} method - Payment method code
 * @returns {string} Formatted payment method name
 */
export const formatPaymentMethod = (method) => {
  const methodNames = {
    'CASH': 'Tunai',
    'QRIS': 'QRIS',
    'TRANSFER': 'Transfer Bank',
    'MIXED': 'Campuran'
  }
  return methodNames[method] || method
}

/**
 * Format order status for display
 * @param {string} status - Order status code
 * @returns {Object} Status with label and color class
 */
export const formatOrderStatus = (status) => {
  const statusConfig = {
    'PENDING': { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    'DONE': { label: 'Selesai', color: 'bg-green-100 text-green-800' },
    'CANCELLED': { label: 'Dibatalkan', color: 'bg-red-100 text-red-800' }
  }
  return statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' }
}

/**
 * Format payment status for display
 * @param {string} status - Payment status code
 * @returns {Object} Status with label and color class
 */
export const formatPaymentStatus = (status) => {
  const statusConfig = {
    'UNPAID': { label: 'Belum Bayar', color: 'bg-red-100 text-red-800' },
    'PARTIAL': { label: 'Bayar Sebagian', color: 'bg-yellow-100 text-yellow-800' },
    'PAID': { label: 'Lunas', color: 'bg-green-100 text-green-800' },
    'REFUNDED': { label: 'Dikembalikan', color: 'bg-blue-100 text-blue-800' }
  }
  return statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' }
}

// ==================== DATE HELPERS ====================

/**
 * Generate transaction number format
 * @param {Date} date - Transaction date
 * @param {number} sequence - Daily sequence number
 * @returns {string} Transaction number (TRX-YYYY-MM-DD-NNNN)
 */
export const generateTransactionNumber = (date = new Date(), sequence = 1) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const seq = String(sequence).padStart(4, '0')
  
  return `TRX-${year}-${month}-${day}-${seq}`
}

/**
 * Format date for API calls (YYYY-MM-DD)
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForAPI = (date) => {
  if (!date) return ''
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

/**
 * Format date and time for display
 * @param {string|Date} dateTime - DateTime to format
 * @returns {string} Formatted date time string
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return ''
  const d = new Date(dateTime)
  return d.toLocaleString('id-ID', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// ==================== CART HELPERS ====================

/**
 * Add item to cart with quantity management
 * @param {Array} cart - Current cart items
 * @param {Object} newItem - Item to add
 * @returns {Array} Updated cart
 */
const areAddonsIdentical = (addonsA = [], addonsB = []) => {
  if (addonsA.length !== addonsB.length) return false;
  const mapA = {};
  addonsA.forEach(a => {
    mapA[a.id] = a.qty || 1;
  });
  for (const b of addonsB) {
    if ((mapA[b.id] || 0) !== (b.qty || 1)) {
      return false;
    }
  }
  return true;
};

export const addToCart = (cart, newItem) => {
  const existingItemIndex = cart.findIndex(item => {
    if (newItem.item_type === 'PRODUCT') {
      const itemProductId = item.product_id || item.id;
      const newItemProductId = newItem.product_id || newItem.id;
      
      const itemVariantId = item.variant_id || item.product_variant_id || null;
      const newItemVariantId = newItem.variant_id || newItem.product_variant_id || null;
      
      const sameProduct = itemProductId === newItemProductId;
      const sameVariant = itemVariantId === newItemVariantId;
      const sameUnit = (item.unit_id || null) === (newItem.unit_id || null);
      const sameSellingUnit = (item.selling_unit_id || null) === (newItem.selling_unit_id || null);
      const sameAddons = areAddonsIdentical(item.selected_options || [], newItem.selected_options || []);

      return sameProduct && sameVariant && sameUnit && sameSellingUnit && sameAddons;
    } else {
      return item.service_id === newItem.service_id;
    }
  });
  
  if (existingItemIndex >= 0) {
    const updatedCart = [...cart]
    const existingItem = updatedCart[existingItemIndex]
    const qtyToAdd = newItem.quantity || newItem.qty || 1
    const nextQuantity = Math.round(((existingItem.quantity || 0) + qtyToAdd) * 1000) / 1000

    const updatedItem = {
      ...existingItem,
      quantity: nextQuantity,
      qty: nextQuantity,
    }
    updatedCart[existingItemIndex] = {
      ...updatedItem,
      total_price: calculateOrderItemTotals(updatedItem).subtotal,
    }
    return updatedCart
  } else {
    const qty = newItem.quantity || newItem.qty || 1;
    const itemToAdd = {
      ...newItem,
      quantity: qty,
      qty: qty,
    }
    return [...cart, {
      ...itemToAdd,
      total_price: calculateOrderItemTotals(itemToAdd).subtotal,
    }];
  }
}

/**
 * Update cart item quantity
 * @param {Array} cart - Current cart items
 * @param {number} itemIndex - Index of item to update
 * @param {number} newQuantity - New quantity
 * @returns {Array} Updated cart
 */
export const updateCartItemQuantity = (cart, itemIndex, newQuantity) => {
  if (itemIndex < 0 || itemIndex >= cart.length) return cart
  
  const updatedCart = [...cart]
  if (newQuantity <= 0) {
    // Remove item if quantity is 0 or less
    updatedCart.splice(itemIndex, 1)
  } else {
    const cleanQty = Math.round(Number(newQuantity) * 1000) / 1000
    const updatedItem = {
      ...updatedCart[itemIndex],
      quantity: cleanQty,
      qty: cleanQty,
    }
    updatedCart[itemIndex] = {
      ...updatedItem,
      total_price: calculateOrderItemTotals(updatedItem).subtotal,
    }
  }
  
  return updatedCart
}

/**
 * Remove item from cart
 * @param {Array} cart - Current cart items
 * @param {number} itemIndex - Index of item to remove
 * @returns {Array} Updated cart
 */
export const removeFromCart = (cart, itemIndex) => {
  if (itemIndex < 0 || itemIndex >= cart.length) return cart
  
  const updatedCart = [...cart]
  updatedCart.splice(itemIndex, 1)
  return updatedCart
}

/**
 * Clear entire cart
 * @returns {Array} Empty cart array
 */
export const clearCart = () => {
  return []
}

// ==================== SEARCH & FILTER HELPERS ====================

/**
 * Build query parameters for API calls
 * @param {Object} filters - Filter object
 * @returns {URLSearchParams} Query parameters
 */
export const buildQueryParams = (filters) => {
  const params = new URLSearchParams()
  
  Object.keys(filters).forEach(key => {
    if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
      params.append(key, filters[key])
    }
  })
  
  return params
}

/**
 * Generate date range for common periods
 * @param {string} period - Period type ('today', 'week', 'month', 'year')
 * @returns {Object} Start and end dates
 */
export const getDateRangeForPeriod = (period) => {
  const now = new Date()
  let startDate, endDate
  
  switch (period) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      break
    case 'week':
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()))
      startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate())
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      break
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
      break
    case 'year':
      startDate = new Date(now.getFullYear(), 0, 1)
      endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59)
      break
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      endDate = now
  }
  
  return {
    start_date: formatDateForAPI(startDate),
    end_date: formatDateForAPI(endDate)
  }
}
